/**
 * ตัวส่ง batch ของคิวออฟไลน์ไปที่ `POST /v1/offline-sync/commands` (D-076/D-078)
 *
 * แยกจาก `syncEngine.ts` เพราะสองเรื่องนี้พังคนละแบบและต้องทดสอบแยกกัน: ที่นี่คือ
 * "รูปร่างของ request/response ตาม contract" ส่วนที่นั่นคือ "ลำดับของ lifecycle
 * ในคิว" — engine ที่ปนกับ parser จะทำให้ response ที่อ่านไม่ออกกลายเป็นสถานะ
 * ปลายทางในเครื่องได้โดยไม่มีใครเห็น
 *
 * หลักการเดียวกับ client ตัวอื่นของแอป (`lib/goodsReceipts/client.ts`):
 *   - **ไม่ throw** ทุกความล้มเหลวกลายเป็นสถานะที่ผู้เรียกต้องอ่าน
 *   - ไม่เติม `Authorization` เอง — `apiFetch` เป็นจุดเดียวที่ทำ (และเป็นจุดเดียว
 *     ที่ดัก `401` ตาม D-049)
 *   - response ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่มีผลลัพธ์" — สองอย่างนี้
 *     ต่างกันคนละขั้ว เพราะอย่างหลังจะทำให้คำสั่งถูกคืนเข้าคิวทั้งที่ server รับไปแล้ว
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย: สิทธิ์ทุกคำสั่งถูกตัดสินที่ server ตาม action จริงของ
 * คำสั่งนั้น (`docs/API_CONTRACT.md` — Offline Sync Commands Payload Contract)
 */

import { parseServerSyncStatus, type OfflineCommandRecord, type ServerSyncStatus } from './commandEnvelope';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import { apiFetch } from '../auth/authorizedFetch';

/** เส้นทางจริง — ตาราง endpoint ใน contract ตัด prefix `/v1` ออก แต่ client ใส่เสมอ */
export const OFFLINE_SYNC_COMMANDS_PATH = '/v1/offline-sync/commands';

/**
 * คำสั่งหนึ่งใบในรูปที่ contract รับ — หกฟิลด์ ไม่ขาดไม่เกิน
 *
 * `extra="forbid"` ฝั่ง server แปลว่าฟิลด์ที่เกินมาทำให้ **ทั้ง batch** ถูกปฏิเสธ
 * ด้วย `400` — `employee_id` บน envelope ในเครื่องจึงต้องไม่ถูกส่งไปด้วย: ผู้กระทำ
 * ที่ server บันทึกมาจาก auth context เสมอ ไม่ใช่จากค่าที่เครื่องส่งขึ้นไป
 */
export interface SyncCommandEnvelope {
  readonly client_event_id: string;
  readonly occurred_at_device: string;
  readonly device_sequence_no: number;
  readonly command_type: string;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly payload_hash: string;
}

export interface SyncCommandsBody {
  readonly device_id: string;
  readonly commands: readonly SyncCommandEnvelope[];
}

/** ผลรายคำสั่งตาม contract — `detail` คือ rule token ของการปฏิเสธ ไม่ใช่ประโยค */
export interface SyncCommandResult {
  readonly client_event_id: string;
  readonly device_sequence_no: number;
  readonly sync_status: ServerSyncStatus;
  readonly detail: string | null;
}

/**
 * ความล้มเหลวของ transport — **ไม่มีอันไหนเป็นคำตอบเกี่ยวกับคำสั่ง**
 *
 * แยกจากกันเพราะสิ่งที่ผู้ใช้ต้องทำต่างกัน: `unauthenticated` ต้องล็อกอินใหม่,
 * `rejected` แปลว่า batch นี้ผิดรูป/ไม่มีสิทธิ์ (ส่งซ้ำเหมือนเดิมจะได้ผลเดิม) ส่วน
 * `network-error`/`server-error` คือ "ยังไม่รู้ผล" ซึ่งต้องลองใหม่
 */
export type SyncTransportFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'rejected'; readonly httpStatus: number; readonly rule: string | null }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' };

export type SyncCommandsResult =
  | { readonly status: 'results'; readonly results: readonly SyncCommandResult[] }
  | SyncTransportFailure;

export interface SyncRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ — ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับ client อื่น */
  readonly fetchImpl?: typeof fetch;
}

/* ------------------------------------------------------------ payload hash -- */

/**
 * JSON ที่เรียงคีย์แล้วและไม่มีช่องว่าง
 *
 * ต้อง deterministic เพราะเป็นสิ่งที่ถูกย่อยเป็น `payload_hash`: คำสั่งใบเดิมที่ถูก
 * ส่งซ้ำหลัง reload ต้องได้ค่าเดียวกับครั้งแรก ไม่ใช่ค่าใหม่เพราะลำดับคีย์เปลี่ยน
 */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  }

  if (value !== null && typeof value === 'object') {
    const fields = Object.entries(value as Record<string, unknown>)
      .filter(([, child]) => child !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([key, child]) => `${JSON.stringify(key)}:${canonicalJson(child)}`);
    return `{${fields.join(',')}}`;
  }

  return JSON.stringify(value) ?? 'null';
}

/**
 * ค่านำหน้าของ digest สำรอง — ต้องแยกออกจาก SHA-256 ด้วยตาเปล่า
 *
 * `crypto.subtle` มีเฉพาะใน secure context เครื่องหน้างานที่เปิดผ่าน `http://`
 * ในวง LAN จึงไม่มี ถ้าเงียบ ๆ ส่งค่าที่ดูเหมือน hex 64 ตัวออกไป วันหนึ่งที่มีคน
 * ตรวจ digest ฝั่ง server จะตีความว่า "เครื่องนี้แฮชผิด" แทนที่จะเป็น "เครื่องนี้
 * แฮชด้วยวิธีอื่น"
 */
export const FALLBACK_PAYLOAD_HASH_PREFIX = 'fnv1a32-';

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function fnv1a32(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * digest ของ payload ที่เครื่องคำนวณเอง
 *
 * server **ไม่ได้อ่านค่านี้เพื่อตัดสิน dedup** — มันคำนวณ digest ของ payload ที่
 * ได้รับเองเสมอ (`modules/offline/service.py: payload_hash_of`) เพราะ digest ที่
 * อุปกรณ์ส่งมาเองยืนยันอะไรไม่ได้ ค่านี้จึงมีไว้ให้ contract ครบรูป และเป็นที่ที่
 * Work Order ของ device-auth จะเอาลายเซ็นมาแปะในอนาคต
 */
export async function payloadHashOf(payload: unknown): Promise<string> {
  const canonical = canonicalJson(payload);
  const subtle = (globalThis as { crypto?: Crypto }).crypto?.subtle;

  if (subtle !== undefined && typeof TextEncoder !== 'undefined') {
    try {
      const digest = await subtle.digest('SHA-256', new TextEncoder().encode(canonical));
      return toHex(new Uint8Array(digest));
    } catch {
      /* ไม่มี SHA-256 ใน runtime นี้ — ใช้ digest สำรองที่บอกตัวเองว่าเป็นคนละอย่าง */
    }
  }

  return `${FALLBACK_PAYLOAD_HASH_PREFIX}${fnv1a32(canonical)}`;
}

/* -------------------------------------------------------------- serialize -- */

/**
 * แปลงแถวในคิวเป็น envelope ของ contract
 *
 * เขียนทีละคีย์ ไม่ spread แถวทั้งก้อน — แถวในเครื่องมี `device_id`/`employee_id`
 * ติดอยู่ด้วย และทั้งคู่ไม่อยู่ใน envelope ที่ server รับ (`device_id` อยู่ระดับ
 * batch, `employee_id` มาจาก auth context)
 */
export async function toSyncCommandEnvelope(
  record: OfflineCommandRecord,
): Promise<SyncCommandEnvelope> {
  return {
    client_event_id: record.client_event_id,
    occurred_at_device: record.occurred_at_device,
    device_sequence_no: record.device_sequence_no,
    command_type: record.command_type,
    payload: record.payload,
    payload_hash: await payloadHashOf(record.payload),
  };
}

/** body ของ batch — คำสั่งเรียงตาม `device_sequence_no` ตามที่ contract กำหนด */
export async function buildSyncCommandsBody(
  deviceId: string,
  records: readonly OfflineCommandRecord[],
): Promise<SyncCommandsBody> {
  const commands = await Promise.all(records.map((record) => toSyncCommandEnvelope(record)));
  return { device_id: deviceId, commands };
}

/* ----------------------------------------------------------------- parse --- */

function ruleOf(raw: unknown): string | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const details = (raw as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null) {
    return null;
  }
  const rule = (details as { rule?: unknown }).rule;
  return typeof rule === 'string' && rule.trim() !== '' ? rule : null;
}

/**
 * อ่าน `{ results: [...] }` — ผิดรูปแม้แถวเดียวคือ **ทั้งคำตอบใช้ไม่ได้**
 *
 * ไม่รับผลบางส่วนโดยเจตนา: ถ้ารับเฉพาะแถวที่อ่านออก คำสั่งที่เหลือจะถูกคืนเข้าคิว
 * แล้วส่งใหม่ทั้งที่ server อาจตัดสินไปแล้ว — ปลอดภัยเพราะ dedupe ก็จริง แต่สถานะ
 * บนหน้าจอจะเล่าเรื่องที่ไม่ตรงกับความจริงฝั่ง server
 */
export function parseSyncCommandResults(raw: unknown): SyncCommandResult[] | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const entries = (raw as { results?: unknown }).results;
  if (!Array.isArray(entries)) {
    return null;
  }

  const results: SyncCommandResult[] = [];
  for (const entry of entries) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      return null;
    }
    const row = entry as Record<string, unknown>;

    const clientEventId = row.client_event_id;
    const sequenceNo = row.device_sequence_no;
    const syncStatus = parseServerSyncStatus(row.sync_status);
    const detail = row.detail;

    if (
      typeof clientEventId !== 'string' ||
      clientEventId.trim() === '' ||
      typeof sequenceNo !== 'number' ||
      !Number.isSafeInteger(sequenceNo) ||
      syncStatus === null ||
      !(detail === null || detail === undefined || typeof detail === 'string')
    ) {
      return null;
    }

    results.push({
      client_event_id: clientEventId,
      device_sequence_no: sequenceNo,
      sync_status: syncStatus,
      detail: typeof detail === 'string' ? detail : null,
    });
  }

  return results;
}

/* --------------------------------------------------------------- request --- */

/**
 * ส่ง batch หนึ่งชุด
 *
 * `cache: 'no-store'` เหมือน client ตัวอื่น — คำตอบของการซิงค์ต้องไม่ถูกหยิบจาก
 * cache ของเบราว์เซอร์ เพราะผลของคำสั่งเป็นเรื่องที่เกิดขึ้นครั้งเดียว
 */
export async function postOfflineSyncCommands(
  body: SyncCommandsBody,
  options: SyncRequestOptions = {},
): Promise<SyncCommandsResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const url = buildApiUrl(options.apiBaseUrl ?? getConfiguredApiBaseUrl(), OFFLINE_SYNC_COMMANDS_PATH);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (response.status === 401) {
    return { status: 'unauthenticated' };
  }

  if (response.status >= 400 && response.status < 500) {
    let rule: string | null = null;
    try {
      rule = ruleOf(await response.json());
    } catch {
      /* error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว */
    }
    return { status: 'rejected', httpStatus: response.status, rule };
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  const results = parseSyncCommandResults(payload);
  return results === null ? { status: 'invalid-response' } : { status: 'results', results };
}
